<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import V2SetupPickerList from '@/components/v2/store-setup/shared/V2SetupPickerList.vue';
  import { currencyOptions } from '@/domain/store-setup/business-step';
  import {
    currencyPluralName,
    currencySymbol,
  } from '@/domain/store-setup/money';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Precios en pesos argentinos ($)» (plan §4.2 item 5, D7): the currency
   * comes from the country and «Cambiar» offers the 14 currencies. Venezuela
   * has no default: the owner picks bolívares or dollars before creating.
   */
  const VENEZUELA = 'VE';

  const props = withDefaults(
    defineProps<{
      country: string;
      value?: string | null;
      disabled?: boolean;
    }>(),
    { value: null, disabled: false }
  );
  const emit = defineEmits<{ (e: 'input', code: string): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const anchor = ref<HTMLElement | null>(null);
  const questionId = nextDomId('setup-currency-question');

  const locale = computed(() => proxy.$i18n.locale);
  const venezuela = computed(() => props.country === VENEZUELA);
  const options = computed(() => currencyOptions(locale.value));
  const currencyName = computed(() =>
    props.value ? currencyPluralName(props.value, locale.value) : ''
  );
  const symbol = computed(() =>
    props.value ? currencySymbol(props.value) : ''
  );
  /** Segment ids are the currency codes; '' while unset (PosUiSegmented needs a string). */
  const segments = computed(() => [
    { id: 'VES', label: tr('storeSetup.business.currencyVes') },
    { id: 'USD', label: tr('storeSetup.business.currencyUsd') },
  ]);
  const venezuelaValue = computed(() =>
    props.value === 'VES' || props.value === 'USD' ? props.value : ''
  );

  async function pick(code: string, close: () => void) {
    close();
    if (code !== props.value) emit('input', code);
    // The focused search box went away with the panel: give focus back to «Cambiar».
    await nextTick();
    const trigger = anchor.value?.querySelector('.currency-change');
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
  }

  function onSegment(code: string) {
    if (!props.disabled && code !== props.value) emit('input', code);
  }
</script>

<template>
  <div ref="anchor" class="v2-setup-currency-field">
    <div v-if="venezuela" class="currency-box is-question">
      <p :id="questionId" class="currency-question">
        {{ $t('storeSetup.business.currencyQuestion') }}
      </p>
      <PosUiSegmented
        class="currency-segments"
        :value="venezuelaValue"
        :items="segments"
        :disabled="disabled"
        :aria-labelledby="questionId"
        @input="onSegment"
      />
      <p v-if="!venezuelaValue" class="currency-required">
        {{ $t('storeSetup.business.currencyRequired') }}
      </p>
    </div>
    <div v-else class="currency-box">
      <i18n
        path="storeSetup.business.currencyLine"
        tag="p"
        class="currency-line"
      >
        <template #currency
          ><strong class="currency-name">{{ currencyName }}</strong></template
        >
        <template #symbol>{{ symbol }}</template>
      </i18n>
      <PosUiPopover
        placement="bottom"
        align="end"
        :disabled="disabled"
        class="currency-popover"
      >
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="currency-change"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :disabled="disabled"
            @click="toggle"
          >
            {{ $t('storeSetup.business.currencyChange') }}
          </button>
        </template>
        <template #default="{ close }">
          <V2SetupPickerList
            :options="options"
            :value="value"
            :search-label="$t('posV2.search')"
            :list-label="$t('storeSetup.business.currencyQuestion')"
            :empty-text="$t('posV2.noResults')"
            @select="(code) => pick(code, close)"
          />
        </template>
      </PosUiPopover>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-currency-field {
    @apply tw-flex tw-min-w-0 tw-flex-col;
    .currency-box {
      @apply tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-4 tw-py-2;
      // White: the segmented track (neutral-100) would vanish on the canvas well.
      &.is-question {
        @apply tw-flex-col tw-items-start tw-gap-2 tw-bg-white tw-py-3;
      }
    }
    .currency-line {
      @apply tw-m-0 tw-min-w-0 tw-text-sm tw-text-neutral-700;
    }
    .currency-name {
      @apply tw-font-medium tw-text-neutral-900;
    }
    .currency-change {
      @apply tw-min-h-11 tw-shrink-0 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .currency-popover {
      @apply tw-shrink-0;
    }
    .currency-question {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .currency-segments {
      @apply tw-max-w-full;
    }
    .currency-required {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
